import {Container, Singleton} from 'typescript-ioc'
import BaseApi from '@/api/BaseApi'
import Paging, {PagingQueryForm} from "biz-ts/src/api/Paging";
import Device, {DeviceDetail, DevicePropStatus, DeviceShare, DregisterOptions} from "@/model/Device";
import {
    DbaqForm,
    DbatchGenerateForm,
    DbindForm,
    DeditForm,
    DimportForm,
    DpqForm,
    DregisterForm,
    DshareForm,
    DsharePqForm,
    DunshareForm
} from "@/form/DeviceForm";
import Topic from "@/module/device/Topic";
import TaskCreate from "@/module/task/TaskCreate";
import {DeviceControl, DeviceControlTmpl} from "@/module/device/DeviceControlTmpl";
import _ from 'lodash';
import DeviceUpgradeInfo from "@/module/device/DeviceUpgradeInfo";

@Singleton
export default abstract class DeviceApi {

    abstract query(id: number): Promise<Device>

    abstract detail(id: number): Promise<DeviceDetail>

    abstract paging(form: PagingQueryForm): Promise<Paging<Device>>

    abstract registerOptions(productId: number): Promise<DregisterOptions>

    abstract register(form: DregisterForm): Promise<Device>

    abstract edit(form: DeditForm): Promise<Device>

    abstract delete(id: number): Promise<void>

    /**
     * @deprecated
     */
    abstract import(form: DimportForm): Promise<TaskCreate>

    abstract batchDelete(ids: Array<number>): Promise<TaskCreate>

    /**
     * @deprecated
     */
    abstract batchGenerate(form: DbatchGenerateForm): Promise<TaskCreate>

    abstract propStatuses(id: number): Promise<Array<DevicePropStatus>>

    abstract topics(id: number): Promise<Topic>

    abstract sharePaging(form: DsharePqForm): Promise<Paging<DeviceShare>>

    abstract share(form: DshareForm): Promise<void>

    abstract unshare(form: DunshareForm): Promise<void>

    abstract bind(form: DbindForm): Promise<void>

    abstract unbind(id: number): Promise<void>

    abstract baq(form: DbaqForm): Promise<void>

    abstract controlTmpls(id: number): Promise<Array<DeviceControlTmpl>>

    abstract control(control: DeviceControl): Promise<void>

    abstract detectUpgrade(id: number): Promise<DeviceUpgradeInfo>

    abstract upgrade(id: number, topic?: string): Promise<void>
}

class DeviceApiImpl extends BaseApi implements DeviceApi {

    protected urlPrefix = 'device'

    query(id: number): Promise<Device> {
        return this.axios.post('query', {id: id})
    }

    detail(id: number): Promise<DeviceDetail> {
        return this.axios.post('detail', {id: id})
    }

    paging(form: DpqForm): Promise<Paging<Device>> {
        if (_.isEmpty(form.id)) form.id = undefined
        if (_.isEmpty(form.name)) form.name = undefined
        if (_.isEmpty(form.mac)) form.mac = undefined
        if (_.isEmpty(form.username)) form.username = undefined
        return this.axios.post('paging', form)
    }

    register(form: DregisterForm): Promise<Device> {
        return this.axios.post('register', form)
    }

    registerOptions(productId: number): Promise<DregisterOptions> {
        return this.axios.post('registerOptions', {productId: productId})
    }

    edit(form: DeditForm): Promise<Device> {
        return this.axios.post('edit', form)
    }

    delete(id: number): Promise<void> {
        return this.axios.post('delete', {id: id})
    }

    import(form: DimportForm): Promise<TaskCreate> {
        return this.axios.post('import', form)
    }

    batchDelete(ids: Array<number>): Promise<TaskCreate> {
        return this.axios.post('batchDelete', {ids: ids})
    }

    batchGenerate(form: DbatchGenerateForm): Promise<TaskCreate> {
        return this.axios.post('batchGenerate', form)
    }

    propStatuses(id: number): Promise<Array<DevicePropStatus>> {
        return this.axios.post('propStatuses', {id: id})
    }

    topics(id: number): Promise<Topic> {
        return this.axios.get('topics', {params: {id: id}})
    }

    sharePaging(form: DsharePqForm): Promise<Paging<DeviceShare>> {
        return this.axios.get('sharePaging', {params: form})
    }

    share(form: DshareForm): Promise<void> {
        return this.axios.post('share', form)
    }

    unshare(form: DunshareForm): Promise<void> {
        return this.axios.post('unshare', form)
    }

    bind(form: DbindForm): Promise<void> {
        return this.axios.post('bind', form)
    }

    unbind(id: number): Promise<void> {
        return this.axios.post('unbind', {deviceId: id})
    }

    baq(form: DbaqForm): Promise<void> {
        return this.axios.post('baq', form)
    }

    controlTmpls(id: number): Promise<Array<DeviceControlTmpl>> {
        return this.axios.get('controlTmpls', {params: {id: id}})
    }

    control(control: DeviceControl): Promise<void> {
        return this.axios.post('control', {control: control})
    }

    detectUpgrade(id: number): Promise<DeviceUpgradeInfo> {
        return this.axios.get('detectUpgrade', {params: {id: id}})
    }

    upgrade(id: number, topic: string | undefined = undefined): Promise<void> {
        return this.axios.post('upgrade', {id: id, topic: topic})
    }
}

Container.bind(DeviceApi).to(DeviceApiImpl)
